/**
 * API endpoint definitions
 */

export const ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: "/api/auth/login",
    LOGOUT: "/api/auth/logout",
    REFRESH: "/api/auth/refresh",
    REGISTER: "/api/auth/register",
    ME: "/api/auth/me",
    GOOGLE: "/api/auth/google",
    GOOGLE_CALLBACK: "/api/auth/google/callback",
  },

  // Patients
  PATIENTS: {
    LIST: "/api/patients",
    CREATE: "/api/patients",
    GET: (id) => `/api/patients/${id}`,
    UPDATE: (id) => `/api/patients/${id}`,
    DELETE: (id) => `/api/patients/${id}`,
    SEARCH: "/api/patients/search",
  },

  // Services
  SERVICES: {
    LIST: "/api/services",
    CREATE: "/api/services",
    GET: (id) => `/api/services/${id}`,
    UPDATE: (id) => `/api/services/${id}`,
    DELETE: (id) => `/api/services/${id}`,
  },

  // Appointments
  APPOINTMENTS: {
    LIST: "/appointments",
    CREATE: "/appointments",
    GET: (id) => `/appointments/${id}`,
    UPDATE: (id) => `/appointments/${id}`,
    DELETE: (id) => `/appointments/${id}`,
    BY_DATE: "/appointments/by-date",
    BY_PATIENT: (patientId) => `/appointments/patient/${patientId}`,
    AVAILABLE_SLOTS: "/appointments/available-slots",
  },

  // Doctor Availability
  DOCTOR_AVAILABILITY: {
    LIST: "/doctor-availability",
    CREATE: "/doctor-availability",
    GET: (id) => `/doctor-availability/${id}`,
    UPDATE: (id) => `/doctor-availability/${id}`,
    DELETE: (id) => `/doctor-availability/${id}`,
    BY_DOCTOR: (doctorId) => `/doctor-availability/doctor/${doctorId}`,
  },

  // Room Availability
  ROOM_AVAILABILITY: {
    LIST: "/room-availability",
    CREATE: "/room-availability",
    GET: (id) => `/room-availability/${id}`,
    UPDATE: (id) => `/room-availability/${id}`,
    DELETE: (id) => `/room-availability/${id}`,
    BY_ROOM: (roomId) => `/room-availability/room/${roomId}`,
  },

  // Blood Tests
  BLOOD_TESTS: {
    LIST: "/blood-tests",
    CREATE: "/blood-tests",
    GET: (id) => `/blood-tests/${id}`,
    UPDATE: (id) => `/blood-tests/${id}`,
    DELETE: (id) => `/blood-tests/${id}`,
    BY_PATIENT: (patientId) => `/blood-tests/patient/${patientId}`,
    UPLOAD: "/blood-tests/upload",
  },

  // Clinical Notes
  CLINICAL_NOTES: {
    LIST: "/api/clinical-notes",
    CREATE: "/api/clinical-notes",
    GET: (id) => `/api/clinical-notes/${id}`,
    UPDATE: (id) => `/api/clinical-notes/${id}`,
    DELETE: (id) => `/api/clinical-notes/${id}`,
    BY_PATIENT: (patientId) => `/api/clinical-notes/patient/${patientId}`,
  },

  // Prescriptions
  PRESCRIPTIONS: {
    LIST: "/api/prescriptions",
    CREATE: "/api/prescriptions",
    GET: (id) => `/api/prescriptions/${id}`,
    UPDATE: (id) => `/api/prescriptions/${id}`,
    DELETE: (id) => `/api/prescriptions/${id}`,
    BY_PATIENT: (patientId) => `/api/prescriptions/patient/${patientId}`,
    DISPENSE: (id) => `/api/prescriptions/${id}/dispense`,
    GENERATE_INVOICE: (id) => `/api/prescriptions/${id}/generate-invoice`,
    INVOICES: (id) => `/api/prescriptions/${id}/invoices`,
    UNINVOICED_DISPENSES: (id) => `/api/prescriptions/${id}/uninvoiced-dispenses`,
  },

  // Billing & Invoices
  BILLING: {
    INVOICES: "/api/billing/invoices",
    INVOICE_DETAIL: (id) => `/api/billing/invoices/${id}`,
    CREATE_INVOICE: "/api/billing/invoices",
    UPDATE_INVOICE: (id) => `/api/billing/invoices/${id}`,
    DELETE_INVOICE: (id) => `/api/billing/invoices/${id}`,
    FINALIZE: (id) => `/api/billing/invoices/${id}/finalize`,
    SYNC: (id) => `/api/billing/invoices/${id}/sync`,
    XERO_CONNECT: "/api/billing/xero/connect",
    XERO_STATUS: "/api/billing/xero/status",
    XERO_DISCONNECT: "/api/billing/xero/disconnect",
  },

  // Medicines (Catalog)
  MEDICINES: {
    LIST: "/api/medicines",
    CREATE: "/api/medicines",
    GET: (id) => `/api/medicines/${id}`,
    UPDATE: (id) => `/api/medicines/${id}`,
    DELETE: (id) => `/api/medicines/${id}`,
    BULK_UPLOAD: "/api/medicines/bulk-upload",
    SEARCH: "/api/medicines/search",
  },

  // Inventory
  INVENTORY: {
    LIST: "/api/inventory",
    CREATE: "/api/inventory",
    GET: (id) => `/api/inventory/${id}`,
    UPDATE: (id) => `/api/inventory/${id}`,
    DELETE: (id) => `/api/inventory/${id}`,
    LOW_STOCK: "/api/inventory/alerts/low-stock",
    EXPIRING: "/api/inventory/alerts/expiring",
    TRANSACTIONS: "/api/inventory/transactions",
    RECEIVE_STOCK: "/api/inventory/receive-stock",
    BULK_UPLOAD: "/api/inventory/bulk-upload",
    STOCK_TEMPLATE: "/api/inventory/stock-template",
  },

  // Reports
  REPORTS: {
    PATIENT_REPORT: (patientId) => `/reports/patient/${patientId}`,
    BIOMARKER_TRENDS: (patientId) => `/reports/biomarker-trends/${patientId}`,
    HEALTH_SCORE: (patientId) => `/reports/health-score/${patientId}`,
    PRACTICE_METRICS: "/reports/practice-metrics",
    REVENUE: "/reports/revenue",
  },

  // Users/Staff
  USERS: {
    LIST: "/users",
    CREATE: "/users",
    GET: (id) => `/users/${id}`,
    UPDATE: (id) => `/users/${id}`,
    DELETE: (id) => `/users/${id}`,
    PROFILE: "/users/profile",
    DOCTORS: "/api/users/doctors",
  },

  // Bookings
  BOOKINGS: {
    LIST: "/api/bookings",
    CREATE: "/api/bookings",
    GET: (id) => `/api/bookings/${id}`,
    UPDATE: (id) => `/api/bookings/${id}`,
    DELETE: (id) => `/api/bookings/${id}`,
    BY_DATE: (date) => `/api/bookings/by-date/${date}`,
    BY_PATIENT: (patientId) => `/api/bookings/patient/${patientId}`,
    AVAILABLE_DATES: "/api/bookings/available-dates",
    SLOTS: "/api/bookings/slots",
    AVAILABLE_DOCTORS: "/api/bookings/available-doctors",
  },

  // Unavailability
  UNAVAILABILITY: {
    LIST: "/api/unavailability",
    CREATE: "/api/unavailability",
    GET: (id) => `/api/unavailability/${id}`,
    UPDATE: (id) => `/api/unavailability/${id}`,
    DELETE: (id) => `/api/unavailability/${id}`,
  },

  // Settings
  SETTINGS: {
    CLINIC_PROFILE: "/api/settings/clinic-profile",
    APP_SETTINGS: "/api/settings/app-settings",
    ROOMS: "/api/settings/rooms",
    ROOMS_CALENDAR_DATA: "/api/settings/rooms/calendar-data",
    EQUIPMENT: "/api/settings/equipment",
    PRACTICE: "/api/settings/practice",
  },

  // Analytics
  ANALYTICS: {
    DASHBOARD_STATS: "/analytics/dashboard",
    PATIENT_METRICS: "/analytics/patient-metrics",
    REVENUE_ANALYTICS: "/analytics/revenue",
    APPOINTMENT_ANALYTICS: "/analytics/appointments",
  },

  // File uploads
  FILES: {
    UPLOAD: "/files/upload",
    DELETE: (fileId) => `/files/${fileId}`,
  },
};
